import { isHTTPError } from 'ky'
import type { ClaimedReward, MilestoneStep } from '~/lib/milestones'
import { getUserInfoReady } from '~/store/user-info'
import { api } from './api'

// Response shapes kept exactly as the endpoints return them (user-service
// swagger); every fetcher goes through the cluster-routed client in ./api.

/** `GET /users/:id/referral/code` */
export interface ReferralCode {
  code: string
}

/** `GET /users/:id/referral/progress` */
export interface ReferralProgress {
  invite_count: number
  accumulated_score: number
}

/** `GET /referral/milestones` */
export interface ReferralMilestones {
  version: string
  steps: MilestoneStep[]
}

/** `GET /users/:id/referral/rewards` */
export interface ReferralRewards {
  rewards: ClaimedReward[]
}

/**
 * `GET /users/:id/referral/invitees` — up to the 6 newest invitees' avatars;
 * a null entry is an invitee with no avatar set (see CONTEXT.md "Invitee
 * avatars").
 */
export interface ReferralInvitees {
  avatars: (string | null)[]
}

/**
 * The user's referral binding — "has already entered a code" ≡ bound.
 * The endpoint answers 404 for an unbound user; this shape folds that
 * into a value.
 */
export interface ReferralBinding {
  bound: boolean
  code?: string
}

export type ItemContentType =
  | 'coin'
  | 'gem'
  | 'tree_type'
  | 'sound'
  | 'sunshine'
  | 'land_skin'
  | 'game_pass'
  | 'refresh_ticket'
  | 'activity_token'

/** One granted piece of a delivery; `value` is the amount for coin/gem. */
export interface ItemContent {
  type: ItemContentType
  value: number
  title_key: string
  icon_image_url: string
}

/** A draw whose original prize may have been converted (e.g. duplicate
 *  compensation) into a different final prize. */
export interface DrawResult {
  original_prize: ItemContent
  final_prize: ItemContent
}

/**
 * What a claim/bind actually put into the account (see CONTEXT.md "Reward
 * delivery") — returned by `POST .../referral/binding` and
 * `POST .../referral/rewards/:required_score/claim`.
 */
export interface RewardDelivery {
  granted_contents: ItemContent[]
  draw_results: DrawResult[]
}

export type RedeemErrorReason = 'not_exist' | 'use_own' | 'already_bound'

/** A redeem rejection the sheet maps to its error copy. */
export class RedeemError extends Error {
  readonly reason: RedeemErrorReason

  constructor(reason: RedeemErrorReason) {
    super(`invite code redeem failed: ${reason}`)
    this.name = 'RedeemError'
    this.reason = reason
  }
}

export async function fetchReferralCode(userId: number): Promise<ReferralCode> {
  return api.service.get(`user/users/${userId}/referral/code`).json()
}

export async function fetchReferralProgress(
  userId: number,
): Promise<ReferralProgress> {
  return api.service.get(`user/users/${userId}/referral/progress`).json()
}

export async function fetchReferralMilestones(): Promise<ReferralMilestones> {
  return api.service.get('user/referral/milestones').json()
}

export async function fetchReferralRewards(
  userId: number,
): Promise<ReferralRewards> {
  return api.service.get(`user/users/${userId}/referral/rewards`).json()
}

export async function fetchReferralInvitees(
  userId: number,
): Promise<ReferralInvitees> {
  return api.service.get(`user/users/${userId}/referral/invitees`).json()
}

export async function fetchReferralBinding(
  userId: number,
): Promise<ReferralBinding> {
  try {
    const { code } = await api.service
      .get(`user/users/${userId}/referral/binding`)
      .json<{ code: string; inviter_user_id: number }>()
    return { bound: true, code }
  } catch (error) {
    // 404 is the endpoint's way of saying "has not bound a code".
    if (isHTTPError(error) && error.response.status === 404) {
      return { bound: false }
    }
    throw error
  }
}

const REDEEM_REASON_BY_STATUS: Record<number, RedeemErrorReason> = {
  404: 'not_exist',
  409: 'already_bound',
  422: 'use_own',
}

/**
 * Bind to an inviter's code. Success delivers the bind reward (see
 * CONTEXT.md "Bind reward"); the known rejections become RedeemErrors and
 * anything else (network, 5xx) rethrows for the generic error ladder.
 */
export async function redeemInviteCode(
  userId: number,
  code: string,
): Promise<RewardDelivery> {
  try {
    return await api.service
      .post(`user/users/${userId}/referral/binding`, { json: { code } })
      .json<RewardDelivery>()
  } catch (error) {
    if (isHTTPError(error)) {
      const reason = REDEEM_REASON_BY_STATUS[error.response.status]
      if (reason) throw new RedeemError(reason)
    }
    throw error
  }
}

/**
 * Claim a reached milestone's reward — one atomic call, no "claiming"
 * in-between state (see CONTEXT.md "Milestone claim state").
 */
export async function claimMilestoneReward(
  userId: number,
  requiredScore: number,
): Promise<RewardDelivery> {
  return api.service
    .post(`user/users/${userId}/referral/rewards/${requiredScore}/claim`)
    .json()
}

/**
 * Why a claim failed: both stale-state answers (someone claimed on another
 * device; the score regressed relative to what we rendered) are corrected by
 * refetching, everything else goes to the connection-issue dialog.
 */
export function claimErrorKind(
  error: unknown,
): 'already_claimed' | 'not_reached' | 'other' {
  if (isHTTPError(error)) {
    if (error.response.status === 409) return 'already_claimed'
    if (error.response.status === 404) return 'not_reached'
  }
  return 'other'
}

async function withUser<T>(fn: (userId: number) => Promise<T>): Promise<T> {
  const { user_id } = await getUserInfoReady()
  return fn(user_id)
}

/** Query option factories for `@tanstack/solid-query`. */
export const referralQueries = {
  code: () => ({
    queryKey: ['referral', 'code'] as const,
    queryFn: () => withUser(fetchReferralCode),
  }),
  progress: () => ({
    queryKey: ['referral', 'progress'] as const,
    queryFn: () => withUser(fetchReferralProgress),
  }),
  milestones: () => ({
    queryKey: ['referral', 'milestones'] as const,
    queryFn: () => fetchReferralMilestones(),
  }),
  rewards: () => ({
    queryKey: ['referral', 'rewards'] as const,
    queryFn: () => withUser(fetchReferralRewards),
  }),
  binding: () => ({
    queryKey: ['referral', 'binding'] as const,
    queryFn: () => withUser(fetchReferralBinding),
  }),
  invitees: () => ({
    queryKey: ['referral', 'invitees'] as const,
    queryFn: () => withUser(fetchReferralInvitees),
  }),
}

/** Redeem with the current user; exposed for the sheet's mutation. */
export function redeemInviteCodeAsUser(code: string): Promise<RewardDelivery> {
  return withUser((userId) => redeemInviteCode(userId, code))
}

/** Claim with the current user; exposed for the app's claim mutation. */
export function claimMilestoneRewardAsUser(
  requiredScore: number,
): Promise<RewardDelivery> {
  return withUser((userId) => claimMilestoneReward(userId, requiredScore))
}
